import { Fragment, memo, type ReactNode, useMemo } from 'react';

import { type Board, type PublicBoard } from '@air/api/types';
import { type ImgixImageProps } from '@air/component-imgix-image';
import { Tooltip } from '@air/primitive-tooltip';

import { CustomFieldListWithOverflow } from '~/components/CustomFields/CustomFieldListWithOverflow';
import { BoardGalleryCardMenu } from '~/components/Gallery/BoardGalleryCard/BoardGalleryCardMenu';
import { BoardGalleryCardThumbnail } from '~/components/Gallery/BoardGalleryCard/BoardGalleryCardThumbnail';
import { GalleryCard, type GalleryCardProps } from '~/components/Gallery/GalleryCard/GalleryCard';
import { GalleryCardContent } from '~/components/Gallery/GalleryCard/GalleryCardContent';
import { GalleryCardFooter } from '~/components/Gallery/GalleryCard/GalleryCardFooter';
import { GalleryCardImageWrapper } from '~/components/Gallery/GalleryCard/GalleryCardImageWrapper';
import { SelectedGalleryCardOverlay } from '~/components/Gallery/GalleryCard/SelectedGalleryCardOverlay';
import { GalleryCardCustomFieldsAction } from '~/components/Gallery/GalleryCardCustomFieldsAction';
import { GalleryItemType } from '~/components/Gallery/types';
import { type LibraryCustomField } from '~/components/LibraryBeta/hooks/queries/useLibraryCustomFields';
import { BOARD_CARD, BOARD_CARD_TITLE, BOARD_META } from '~/constants/testIDs';
import { visibleCustomFieldsViewOptionsSelector } from '~/store/configViews/selectors';
import { type SelectableGalleryBoardItem } from '~/store/selectedItems/types';
import { useAirSelector } from '~/utils/ReduxUtils';

export type BoardGalleryCardProps<B extends Board | PublicBoard> = {
  board: B;
  libraryCustomFields?: LibraryCustomField[];
  cardWidth: number;
  onClick: () => void;
  onCmdClickTitle?: () => void;
  ancestors?: ReactNode;
  sizes?: ImgixImageProps['sizes'];
  canViewCustomFields: boolean;
} & Pick<
  GalleryCardProps<SelectableGalleryBoardItem<B>>,
  'getButtonClassName' | 'getItemMenuOptions' | 'getSelectionMenuOptions' | 'isSelectable'
>;

const _BoardGalleryCard = <B extends Board | PublicBoard>({
  board,
  cardWidth,
  getItemMenuOptions,
  getSelectionMenuOptions,
  isSelectable = true,
  libraryCustomFields,
  onClick,
  onCmdClickTitle,
  ancestors,
  sizes,
  canViewCustomFields,
  getButtonClassName,
}: BoardGalleryCardProps<B>) => {
  const visibleCustomFields = useAirSelector(visibleCustomFieldsViewOptionsSelector);

  const popoverCustomFields = useMemo(() => {
    if (libraryCustomFields?.length) {
      return board?.customFields?.filter((board) => {
        return !!libraryCustomFields.find((customField) => customField.id === board.id)?.visible;
      });
    }

    return board.customFields;
  }, [board.customFields, libraryCustomFields]);

  const boardCustomFields = useMemo(() => {
    if (libraryCustomFields?.length) {
      return board.customFields?.filter((boardField) => {
        const libraryField = libraryCustomFields.find((customField) => customField.id === boardField.id);
        const visibleField = visibleCustomFields.find((vcf) => vcf.customFieldId === boardField.id);

        return libraryField?.visible && visibleField;
      });
    }

    return board.customFields?.filter((customField) =>
      visibleCustomFields.find((vcf) => vcf.customFieldId === customField.id),
    );
  }, [board.customFields, libraryCustomFields, visibleCustomFields]);

  const boardTitle = useMemo(() => {
    /**
     * We break the title into parts and add a wbr tag between them to allow
     * the title to break at the underscore character.
     */
    if (board.title.includes('_')) {
      return board.title.split('_').map((part, i, array) => (
        <Fragment key={i}>
          {part}
          {i < array.length - 1 && (
            <>
              <wbr />_<wbr />
            </>
          )}
        </Fragment>
      ));
    }

    return board.title;
  }, [board.title]);

  return (
    <GalleryCard
      testId={BOARD_CARD}
      title={board.title}
      getItemMenuOptions={getItemMenuOptions}
      getSelectionMenuOptions={getSelectionMenuOptions}
      item={{
        id: board.id,
        item: board,
        type: GalleryItemType.board,
      }}
      onClick={onClick}
      isSelectable={isSelectable}
      getButtonClassName={getButtonClassName}
    >
      {({ setIsMenuOpen, isHovering, isSelected }) => (
        <>
          <GalleryCardImageWrapper>
            {board.thumbnails && board.thumbnails.length > 0 && (
              <BoardGalleryCardThumbnail board={board} sizes={sizes} height="100%" />
            )}
          </GalleryCardImageWrapper>
          {isSelected && <SelectedGalleryCardOverlay />}
          <GalleryCardContent>
            <div className="flex">
              {canViewCustomFields && !!popoverCustomFields?.length && (
                <div className="pointer-events-auto mt-2 ml-2">
                  <GalleryCardCustomFieldsAction customFields={popoverCustomFields} />
                </div>
              )}
              {isHovering && (
                <div className="pointer-events-auto mt-2 mr-2 flex grow justify-end">
                  <BoardGalleryCardMenu
                    board={board}
                    getItemMenuOptions={getItemMenuOptions}
                    getSelectionMenuOptions={getSelectionMenuOptions}
                    setIsMenuOpen={setIsMenuOpen}
                  />
                </div>
              )}
            </div>
            <GalleryCardFooter>
              <Tooltip label={board.title} side={'top'} sideOffset={4} align="start">
                <button
                  onClick={(event) => {
                    if ((event.metaKey || event.ctrlKey) && !!onCmdClickTitle) {
                      onCmdClickTitle();
                    } else {
                      onClick();
                    }
                  }}
                  className="pointer-events-auto text-left font-semibold text-20 text-white leading-none hover:underline"
                  data-testid={BOARD_CARD_TITLE}
                  type="button"
                >
                  <div
                    /**
                     * We add a pb-0.5 to the line-clamp-3 to make sure that the
                     * text doesn't get cut off
                     */
                    className="line-clamp-3 break-words pb-0.5"
                  >
                    {boardTitle}
                  </div>
                </button>
              </Tooltip>

              {!!boardCustomFields?.length && (
                <div className="pointer-events-auto">
                  <CustomFieldListWithOverflow
                    className="mt-1 py-1"
                    customFields={boardCustomFields}
                    allowedWidth={cardWidth}
                    hasDarkBackground
                    shouldShowTooltip
                  />
                </div>
              )}

              {!!ancestors && (
                <p className="mt-0.5 pr-7 text-12 text-white" data-testid={BOARD_META}>
                  in&nbsp;{ancestors}
                </p>
              )}
            </GalleryCardFooter>
          </GalleryCardContent>
        </>
      )}
    </GalleryCard>
  );
};

export const BoardGalleryCard = memo(_BoardGalleryCard) as typeof _BoardGalleryCard;
